import React, { useEffect, useState } from 'react';
import { Icon } from './Icons.jsx';

function formatField(value, field) {
  const precision = field.precision ?? 2;
  if (field.precision === 0) return String(Math.round(value));
  return Number(value).toFixed(precision);
}

function NumericField({ field, value, onChange }) {
  const [draft, setDraft] = useState(() => formatField(value, field));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setDraft(formatField(value, field));
  }, [field, focused, value]);

  const commit = (raw) => {
    const parsed = Number(raw);
    if (Number.isFinite(parsed)) {
      onChange(Math.min(field.max, Math.max(field.min, parsed)));
    } else {
      setDraft(formatField(value, field));
    }
  };

  return (
    <input
      className="param-number"
      type="number"
      inputMode="decimal"
      value={draft}
      min={field.min}
      max={field.max}
      step={field.step}
      onFocus={() => setFocused(true)}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={(event) => {
        setFocused(false);
        commit(event.target.value);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.currentTarget.blur();
        }
        if (event.key === 'Escape') {
          setDraft(formatField(value, field));
          event.currentTarget.blur();
        }
      }}
      aria-label={`${field.label}（${field.unit || '无单位'}）`}
    />
  );
}

export function ParameterPanel({ model, params, onChange, onResetDefaults }) {
  const groups = [];
  for (const field of model.schema) {
    let group = groups.find((item) => item.name === field.group);
    if (!group) {
      group = { name: field.group, fields: [] };
      groups.push(group);
    }
    group.fields.push(field);
  }

  return (
    <div className="panel-scroll param-panel">
      <div className="panel-intro">
        <div className="eyebrow">
          <Icon name="sliders" size={14} />
          实验参数
        </div>
        <h2>实时改变实验变量</h2>
        <p>滑块与数值框都会写入物理引擎。初始条件变更时会自动重放，过程参数即时生效。</p>
      </div>

      {groups.map((group) => (
        <section className="param-group" key={group.name}>
          <div className="param-group-title">
            <span>{group.name}</span>
            <span className="group-line" />
          </div>
          {group.fields.map((field) => {
            const value = params[field.key];
            const pct = ((value - field.min) / (field.max - field.min)) * 100;
            return (
              <div className={`param-field ${field.apply === 'reset' ? 'is-initial' : ''}`} key={field.key}>
                <div className="param-head">
                  <div className="param-name">
                    <span className="param-symbol">{field.symbol}</span>
                    <span>{field.label}</span>
                    {field.apply === 'reset' && (
                      <span className="initial-tag" title="修改后会重置实验并重新播放">
                        重放
                      </span>
                    )}
                  </div>
                  <div className="param-value">
                    <NumericField field={field} value={value} onChange={(next) => onChange(field.key, next)} />
                    <span>{field.unit || '—'}</span>
                  </div>
                </div>
                <input
                  className="param-slider"
                  style={{ '--pct': `${Math.min(100, Math.max(0, pct))}%` }}
                  type="range"
                  min={field.min}
                  max={field.max}
                  step={field.step}
                  value={value}
                  onChange={(event) => onChange(field.key, Number(event.target.value))}
                  aria-label={field.label}
                />
                <div className="param-meta">
                  <span>{field.min}</span>
                  <span className="param-hint">{field.description}</span>
                  <span>{field.max}</span>
                </div>
              </div>
            );
          })}
        </section>
      ))}

      {typeof model.describe === 'function' && (
        <div className="param-insight">
          <span className="param-insight-label">
            <Icon name="bolt" size={13} />
            模型预测
          </span>
          <p>{model.describe(params)}</p>
        </div>
      )}

      <div className="param-actions">
        <button type="button" className="ghost-btn" onClick={onResetDefaults}>
          <Icon name="refresh" size={16} />
          恢复默认参数
        </button>
        <span className="param-safe">
          <Icon name="shield" size={14} />
          越界值会被护栏自动修正
        </span>
      </div>
    </div>
  );
}
